import React, { useContext, useEffect, useMemo, useState } from "react";
import { FormSelect } from "react-bootstrap";
import {
  ResponsiveContainer,
  BarChart,
  Bar,
  XAxis,
  YAxis,
  Tooltip,
  Cell,
} from "recharts";

import Utils from "../../../Utils";
import ContextComponent from "../../../AppContext";
import { DashboardUtility } from "./DashboardUtility";

const DISTRIBUTION_COLORS = [
  "#CD5C5C",
  "#FA8072",
  "#BDB76B",
  "#5F9EA0",
  "#66CDAA",
];

const CustomTooltip = ({ active, payload }) => {
  if (!active || !payload?.length) return null;

  return (
    <div className="submission-ranking-tooltip">
      <div className="submission-ranking-tooltip-title">
        {payload[0].payload.name}
      </div>

      <div className="submission-ranking-tooltip-value">
        {payload[0].value} respondents
      </div>
    </div>
  );
};

const MaturityScore = () => {
  const { setLoadingState, setAlert, setCanRedirectToLogin, userData } =
    useContext(ContextComponent);
  const [maturityData, setMaturityData] = useState({
    averageMaturityPercentage: 0,
    totalCompletedReports: 0,
    distribution: [],
  });
  const [canRender, setCanRender] = useState(false);
  const [selectedSite, setSelectedSite] = useState("");
  const [siteOptions, setSiteOptions] = useState([]);

  useEffect(() => {
    loadSites();
  }, []);

  const loadSites = () => {
    const userId = userData?.id;
    DashboardUtility?.loadSitesForReport({
      userId,
      setLoadingState,
      setAlert,
      setCanRedirectToLogin,
      onSuccess: (sortedSites) => {
        setSiteOptions(sortedSites);

        if (sortedSites.length > 0) {
          const defaultSite = sortedSites[0]?.name;

          setSelectedSite(defaultSite);

          loadMaturityScoreData(defaultSite);
        }
      },
    });
  };

  const loadMaturityScoreData = (siteName) => {
    const fetchSuccess = (res) => {
      setLoadingState({
        applyMask: false,
      });

      const formattedDistribution =
        res?.data?.distribution?.map((item, index) => ({
          ...item,
          name: `${item.name}%`,
          color: DISTRIBUTION_COLORS[index],
        })) || [];

      setMaturityData({
        averageMaturityPercentage: res?.data?.averageMaturityPercentage || 0,
        totalCompletedReports: res?.data?.totalCompletedReports || 0,
        distribution: formattedDistribution,
      });

      setCanRender(formattedDistribution.length > 0);
    };

    const fetchFailure = (err) => {
      setLoadingState({
        applyMask: false,
      });

      setAlert({
        show: true,
        message: "Failed to load maturity score data.",
        type: "danger",
      });

      console.error(err);

      setCanRender(false);

      if (err?.redirect) {
        setCanRedirectToLogin(true);
      }
    };

    setLoadingState({
      applyMask: true,
      text: "Loading maturity score data",
    });

    Utils.getMaturityScoreData(siteName).then(fetchSuccess, fetchFailure);
  };

  const handleChangeSiteData = (e) => {
    const siteName = e?.target?.value;

    setSelectedSite(siteName);

    loadMaturityScoreData(siteName);
  };

  return (
    <div className="maturity-score-container">
      <div className="maturity-score-header">
        <div className="maturity-score-header-content">
          <h5 className="maturity-score-title">
            Assessment maturity score analysis
          </h5>

          <p className="maturity-score-subtitle">
            Select an assessment to view its average maturity score and
            respondent score distribution
          </p>
        </div>

        <div className="maturity-score-select-wrapper">
          <FormSelect
            className="maturity-score-select"
            value={selectedSite}
            onChange={handleChangeSiteData}
            title={selectedSite}
          >
            {siteOptions.map((site, index) => (
              <option
                key={site?.id}
                value={site?.name}
                className="maturity-score-option"
              >
                {site?.name}
              </option>
            ))}
          </FormSelect>
        </div>
      </div>

      {canRender ? (
        <div className="maturity-score-card">
          <div className="maturity-score-top-section">
            <div className="maturity-score-value">
              {Math.round(maturityData.averageMaturityPercentage.toFixed(1))}%
            </div>

            <div className="maturity-score-label">Average maturity score</div>
            <div className="maturity-score-total">
              Based on {maturityData?.totalCompletedReports} completed reports
            </div>
          </div>

          <div className="maturity-chart-title">
            Score distribution across respondents
          </div>

          <div className="maturity-chart-wrapper">
            <ResponsiveContainer width="100%" height={320}>
              <BarChart
                data={maturityData.distribution}
                margin={{
                  top: 10,
                  right: 10,
                  left: 10,
                  bottom: 30,
                }}
              >
                <XAxis
                  dataKey="name"
                  tick={{
                    fontSize: 11,
                    fill: "#304697",
                  }}
                  tickLine={false}
                  axisLine={false}
                />

                <YAxis
                  tick={{
                    fontSize: 11,
                    fill: "#304697",
                  }}
                  tickLine={false}
                  axisLine={false}
                  label={{
                    value: "Number of respondents",
                    angle: -90,
                    position: "insideLeft",
                    style: {
                      fontSize: 11,
                      fill: "#304697",
                    },
                  }}
                />

                <Tooltip
                  content={<CustomTooltip />}
                  cursor={false}
                  shared={false}
                />

                <Bar
                  dataKey="respondents"
                  radius={[5, 5, 0, 0]}
                  cursor="pointer"
                >
                  {maturityData.distribution.map((entry, index) => (
                    <Cell key={index} fill={entry?.color} />
                  ))}
                </Bar>
              </BarChart>
            </ResponsiveContainer>
          </div>
        </div>
      ) : (
        <div className="dashboard-no-data-found">No data found</div>
      )}
    </div>
  );
};

export default MaturityScore;
